'use client'

import { Card, CardContent } from '@/components/ui/shadcn/card'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { Loader2, Info, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

const UsageBar = ({ value, limit, label, tierLimit, isCustom = false }) => {
  const tierPercentage = tierLimit
    ? Math.min((value / tierLimit) * 100, 100)
    : 0
  const customPercentage =
    limit && value > tierLimit
      ? Math.min(((value - tierLimit) / limit) * 100, 100)
      : 0
  const customValue = Math.max(value - tierLimit, 0)

  const pct = isCustom ? customPercentage : tierPercentage

  const barColor =
    pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-primary'

  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center justify-between text-sm">
        <span>{label}</span>
        <span>
          {isCustom
            ? customValue.toLocaleString()
            : (value > tierLimit ? tierLimit : value).toLocaleString()}{' '}
          /{' '}
          {tierLimit
            ? isCustom
              ? limit.toLocaleString()
              : tierLimit.toLocaleString()
            : '0'}{' '}
          ({pct.toFixed(1)}%)
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full transition-all', barColor)}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        />
      </div>
    </div>
  )
}

export default function Usage({
  tierDetails,
  billingDetails,
  usage,
  loading,
  error,
}) {
  if (loading) {
    return (
      <div className="vertically-centered">
        <Loader2 className="size-6 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  const currentTier = tierDetails?.find(
    tier => tier.name === billingDetails?.tier,
  )

  if (!currentTier) return null

  return (
    <Card className="gap-3 py-4">
      <CardContent className="px-4">
        <h4 className="text-lg font-semibold mb-3">Usage</h4>

        <h6 className="text-sm font-semibold mb-2">Tier Limits</h6>
        <UsageBar
          value={usage?.totalInputTokens || 0}
          limit={billingDetails?.inputTokenLimit || 0}
          tierLimit={currentTier.inputTokenLimit}
          label="Input Tokens"
        />
        <UsageBar
          value={usage?.totalOutputTokens || 0}
          limit={billingDetails?.outputTokenLimit || 0}
          tierLimit={currentTier.outputTokenLimit}
          label="Output Tokens"
        />
        <UsageBar
          value={usage?.totalMinutes || 0}
          limit={billingDetails?.minuteLimit || 0}
          tierLimit={currentTier.minuteLimit}
          label="Transcription Minutes"
        />

        <h6 className="mt-6 mb-2 flex items-center gap-2 text-sm font-semibold">
          Custom Limits
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="size-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                Custom limits are only used after tier limits are exhausted
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h6>
        <UsageBar
          value={usage?.totalInputTokens || 0}
          limit={billingDetails?.inputTokenLimit || 0}
          tierLimit={currentTier?.inputTokenLimit || 0}
          label="Input Tokens"
          isCustom
        />
        <UsageBar
          value={usage?.totalOutputTokens || 0}
          limit={billingDetails?.outputTokenLimit || 0}
          tierLimit={currentTier?.outputTokenLimit || 0}
          label="Output Tokens"
          isCustom
        />
        <UsageBar
          value={usage?.totalMinutes || 0}
          limit={billingDetails?.minuteLimit || 0}
          tierLimit={currentTier?.minuteLimit || 0}
          label="Transcription Minutes"
          isCustom
        />
      </CardContent>
    </Card>
  )
}
